<template>
  <div class="goods-card" :class="[cssType, { 'daily-card': props.goods.goodsType === 'daily', 'is-soldout': remainCount <= 0 }]">
    <div class="daily-badge" v-if="props.goods.goodsType === 'daily'">
      <span class="badge-text">{{ $t('novShopping.goods.dailyDeals') }}</span>
      <i class="badge-star badge-star--1"></i>
      <i class="badge-star badge-star--2"></i>
    </div>

    <div class="goods-body">
      <div class="goods-price">{{ $t('novShopping.goods.price', { n: props.goods.price }) }}</div>

      <div class="goods-icon">
        <i class="icon-base"></i>
        <img
          v-if="!state.iconError"
          class="icon-prop"
          :src="icon"
          :alt="propName"
          @error="state.iconError = true"
        />
        <span v-else class="icon-fallback">{{ propName.slice(0, 1) }}</span>
      </div>

      <div class="goods-name" :class="{ 'goods-name--long': propName.length > 30 }">
        <div class="goods-name__text">{{ propName }}</div>
        <div class="goods-name__text">{{ props.goods.propCount }}</div>
      </div>

      <div class="goods-btn" :class="{ disabled: !canBuy }" @click="handleClick">
        <i class="btn-bg"></i>
        <span class="btn-text">{{ btnText }}</span>
        <span class="goods-remain" v-if="remainText">{{ remainText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { isMobile } from '@/utils'
import { getPropIcon, getPropName } from '../../utils/prop'

const props = defineProps({
  activityPhase: { type: String, default: 'loading' },
  goods: {
    type: Object,
    required: true,
  },
  // 该商品的个人兑换情况 { purchasedCount, todayCount }
  status: {
    type: Object,
    default: () => ({ purchasedCount: 0, todayCount: 0 }),
  },
  voucherBalance: {
    type: Number,
    default: 0,
  },
  loggedIn: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['exchange'])

const { t } = useI18n()
const cssType = isMobile ? 'mobile' : 'pc'

const state = reactive({
  iconError: false,
})

const icon = computed(() => getPropIcon(props.goods.propId))
const propName = computed(() => getPropName(t, props.goods))

const remainCount = computed(() => {
  const total = Number(props.goods.purchaseLimit) || 0
  const remainTotal = total > 0 ? total - (Number(props.status.purchasedCount) || 0) : Infinity
  const dailyLimit = Number(props.goods.dailyLimit) || 0
  const remainToday = dailyLimit > 0 ? dailyLimit - (Number(props.status.todayCount) || 0) : Infinity
  return Math.max(0, Math.min(remainTotal, remainToday))
})

const canBuy = computed(() => {
  if (props.activityPhase !== 'active') return false
  if (!props.loggedIn) return remainCount.value > 0
  return remainCount.value > 0 && props.voucherBalance >= Number(props.goods.price)
})

const btnText = computed(() => {
  if (props.activityPhase === 'not-start') return t('novShopping.status.notStart')
  if (props.activityPhase === 'ended') return t('novShopping.status.ended')
  if (props.activityPhase === 'loading') return '…'
  if (remainCount.value <= 0) return t('novShopping.goods.soldOut')
  if (props.loggedIn && props.voucherBalance < Number(props.goods.price)) {
    return t('novShopping.goods.notEnoughVoucher')
  }
  return t('novShopping.goods.purchase')
})

const remainText = computed(() => {
  if (!Number.isFinite(remainCount.value)) return ''
  return t('novShopping.goods.remainTimes', { n: remainCount.value })
})

const handleClick = () => {
  if (props.activityPhase !== 'active') return
  emit('exchange', props.goods)
}
</script>

<style lang="less" scoped>
@import '../../styles/tokens.less';

// ===== 基础（750 设计稿基准，移动端最终生效）=====
.goods-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 310px;
  height: 370px;
  box-sizing: border-box;
  padding: 36px 0 28px;
  background: url('~@/views/act/novShopping2026/assets/imgs/goods/card-frame-gold.png') center center / 100% 100% no-repeat;

  &.is-soldout {
    background-image: url('~@/views/act/novShopping2026/assets/imgs/goods/card-frame-soldout.png');

    .goods-price,
    .goods-name__text {
      color: @sp-goods-text-soldout;
    }
  }

  // 每日超值：紫色缎带在卡片顶部居中，宽度随各语言文案自适应。
  .daily-badge {
    position: absolute;
    left: 0;
    right: 0;
    top: -11px;
    width: max-content;
    margin: 0 auto;
    height: 44px;
    display: flex;
    align-items: center;
    z-index: 2;

    .badge-text {
      position: relative;
      height: 44px;
      padding: 0 17px;
      font-size: 30px;
      font-weight: 700;
      line-height: 44px;
      white-space: nowrap;
      color: #fff;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 29px;
        border-radius: 17px;
        background: @sp-badge-purple;
        z-index: -1;
      }
    }

    .badge-star {
      position: absolute;
      display: block;
      background: url('~@/views/act/novShopping2026/assets/imgs/goods/badge-star-1.png') center center / 100% 100% no-repeat;

      &--1 {
        left: 0;
        top: 8px;
        width: 23px;
        height: 23px;
      }

      &--2 {
        right: 0;
        top: 0;
        width: 26px;
        height: 26px;
        background-image: url('~@/views/act/novShopping2026/assets/imgs/goods/badge-star-2.png');
      }
    }
  }

  .goods-body {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .goods-price {
    font-size: 18px;
    line-height: 24px;
    color: @sp-goods-text;
  }

  .goods-icon {
    position: relative;
    width: 128px;
    height: 126px;
    margin-top: -2px;

    // 居中用 margin 而非 translateX(-50%)：避免父级落在半像素时整张切图被重采样发虚
    .icon-base {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 4px;
      width: 120px;
      height: 120px;
      margin: 0 auto;
      background: url('~@/views/act/novShopping2026/assets/imgs/goods/icon-base-gold.png') center center / 100% 100% no-repeat;
    }

    .icon-prop {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      width: 128px;
      height: 128px;
      margin: 0 auto;
      object-fit: contain;
    }

    .icon-fallback {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 8px;
      font-size: 48px;
      text-align: center;
      color: @sp-goods-text;
    }
  }

  &.is-soldout .icon-base {
    background-image: url('~@/views/act/novShopping2026/assets/imgs/goods/icon-base-soldout.png');
  }

  .goods-name {
    width: 260px;
    margin-top: 1px;
    text-align: center;

    &__text {
      font-size: 18px;
      line-height: 24px;
      color: @sp-goods-text;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    &.goods-name--long .goods-name__text {
      font-size: 16px;
      line-height: 20px;
    }
  }

  .goods-btn {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 235px;
    height: 88px;
    margin-top: 22px;
    padding-top: 19px;
    cursor: pointer;

    .btn-bg {
      position: absolute;
      left: 0;
      top: 0;
      width: 235px;
      height: 76px;
      background: url('~@/views/act/novShopping2026/assets/imgs/goods/btn-gold.png') center center / 100% 100% no-repeat;
    }

    .btn-text {
      position: relative;
      font-size: 24px;
      font-weight: 700;
      line-height: 32px;
      color: @sp-goods-text;
    }

    .goods-remain {
      position: relative;
      margin-top: 19px;
      font-size: 14px;
      line-height: 18px;
      color: @sp-goods-remain;
    }

    &.disabled {
      cursor: not-allowed;

      .btn-bg {
        background-image: url('~@/views/act/novShopping2026/assets/imgs/goods/btn-soldout.png');
      }

      .btn-text {
        color: #fff;
      }
    }
  }
}

// ===== PC（设计稿像素值）=====
.pc.goods-card {
  flex: none;
  height: 386px;
  padding-bottom: 40px;
  background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/card-frame-gold.png');

  &.daily-card:not(.is-soldout) {
    background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/card-frame-daily.png');

    .goods-price, .goods-name__text { color: @sp-rule-text; }

    .goods-icon .icon-base {
      background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/icon-base-daily.png');
    }
  }

  &.is-soldout {
    background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/card-frame-soldout.png');

    .goods-price, .goods-name__text { color: @sp-mobile-goods-soldout; }

    .goods-icon .icon-base {
      background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/icon-base-soldout.png');
    }
  }

  .goods-icon .icon-base {
    background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/icon-base-gold.png');
  }

  .goods-body {
    width: 100%;
    height: 100%;
  }

  .goods-name { flex: none; }

  .daily-badge {
    top: -9px;
    height: 36px;

    .badge-text {
      height: 36px;
      padding: 0 12px;
      font-size: 24px;
      line-height: 36px;
      letter-spacing: -0.4px;

      &::before {
        height: 24px;
        border-radius: 14px;
      }
    }

    .badge-star--1 { top: 6px; width: 18px; height: 18px; }
    .badge-star--2 { width: 20px; height: 20px; }
  }

  .goods-btn {
    flex: none;
    margin-top: auto;
    padding-top: 0;

    .btn-bg {
      background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/btn-gold.png');
    }

    &.disabled .btn-bg {
      background-image: url('~@/views/act/novShopping2026/assets/imgs/mobile/goods/btn-soldout.png');
    }

    .btn-text {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: none;
      box-sizing: border-box;
      width: 100%;
      height: 76px;
      padding: 4px 12px 12px;
      line-height: 1.25;
      text-align: center;
      overflow-wrap: anywhere;
    }

    .goods-remain {
      flex: none;
      margin-top: 0;
    }

    &.disabled .btn-text {
      font-size: 20px;
    }
  }
}
</style>

<style lang="less" scoped src="./mobile_style.less"></style>
